import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { DropShadowFilter } from "pixi-filters";
import { IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { GameFont } from "../../../common/GameFont";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { Hand } from "../../../common/components/Hand";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { TournamentIntroResourcePackage } from "../resources/TournamentIntroResourcePackage";
import { YouNoMoreOwnerSceneResult } from "../scenes/YouNoMoreOwnerSceneResult";

export class TournamentIntroPopupView extends BaseScalableView<YouNoMoreOwnerSceneResult> {
    private static readonly HEADER_TEXT = 'EXCITING\nTOURNAMENT ALERT!';
    private static readonly HEADER_ADDITIONAL_TEXT = 'JOIN OUR LATEST 3 DAYS TOURNAMENT AND\nSTAND A CHANCE TO WIN';
    private static readonly WE_GIVE_TEXT = 'WE GIVE TOTAL OF';
    private static readonly TOTAL_OWN_POINTS = '4,000,000';
    private static readonly FOR_FIRST_TEXT = 'FOR FIRST';
    private static readonly WINNERS_TEXT = '138 WINNERS';
    private static readonly FIRST_PLACE_TEXT = 'FIRST PLACE';
    private static readonly WILL_GET_TEXT = 'WILL GET';
    private static readonly GET_OWN_POINTS_TEXT = '150,000';
    private static readonly GLOWED_TEXT = 'CLICK ON TOURNAMENTS ICON\nIN GAME LOBBY TO LEARN MORE';
    private static readonly BUTTON_TEXT = "LET'S GO";

    private readonly commonResources: CommonResourcesPackage;
    private readonly tournamentIntroResources: TournamentIntroResourcePackage;

    private viewBackground: Sprite;
    private topLight: Sprite;
    private leftLight: Sprite;
    private rightLight: Sprite;
    private headerText: Text;
    private headerAdditionalText: Text;
    private giveText: Text;
    private totalOwnPointsText: Text;
    private totalTokenIcon: Sprite;
    private firstText: Text;
    private winsText: Text;
    private willGetText: Text;
    private firstPlaceText: Text;
    private secondAdditonalUsdcText: Text;
    private usdcIcon: Sprite;
    private glowedText: Text;
    private tournamentIcon: Sprite;
    private hand: Hand;
    private button: ButtonWithScaleAnimation;

    public constructor(
        operationControls: PromiseCompletionSource<YouNoMoreOwnerSceneResult>,
        canvasScaler: ICanvasScaler,
        commonResources: CommonResourcesPackage,
        tournamentIntroResources: TournamentIntroResourcePackage,
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.tournamentIntroResources = tournamentIntroResources;
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public async start(): Promise<void> { }

    private build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_lobby_jpg);
        this.viewBackground.eventMode = 'static';
        this.addChild(this.viewBackground);
        this.topLight = new Sprite(this.tournamentIntroResources.assets_views_tournament_intro_top_light_png);
        this.addChild(this.topLight);
        this.leftLight = new Sprite(this.tournamentIntroResources.assets_views_tournament_intro_side_light_png);
        this.addChild(this.leftLight);
        this.rightLight = new Sprite(this.tournamentIntroResources.assets_views_tournament_intro_side_light_png);
        this.rightLight.scale.x *= -1;
        this.addChild(this.rightLight);
        this.headerText = new Text(TournamentIntroPopupView.HEADER_TEXT, new DefaultTextStyle());
        this.headerText.style.align = "center";
        this.headerText.style.fill = ["#ffe302", "#ff9124"];
        this.headerText.style.fontSize = 116;
        this.headerText.style.lineHeight = 92;
        this.headerText.angle = -5;
        this.headerText.skew.x = -0.08;
        this.addChild(this.headerText);
        this.headerAdditionalText = new Text(TournamentIntroPopupView.HEADER_ADDITIONAL_TEXT, new DefaultTextStyle());
        this.headerAdditionalText.style.align = "left";
        this.headerAdditionalText.style.fontSize = 54;
        this.headerAdditionalText.angle = -5;
        this.headerAdditionalText.skew.x = -0.08;
        this.addChild(this.headerAdditionalText);
        this.giveText = new Text(TournamentIntroPopupView.WE_GIVE_TEXT, new DefaultTextStyle());
        this.giveText.style.fontSize = 65;
        this.giveText.angle = -5;
        this.giveText.skew.x = -0.08;
        this.addChild(this.giveText);
        this.totalOwnPointsText = new Text(TournamentIntroPopupView.TOTAL_OWN_POINTS, new DefaultTextStyle());
        this.totalOwnPointsText.style.fill = ["#FFE302", "#FF9024"];
        this.totalOwnPointsText.style.fillGradientType = 0;
        this.totalOwnPointsText.style.fillGradientStops = [0, 0.7, 0.8];
        this.totalOwnPointsText.style.fontSize = 120;
        this.totalOwnPointsText.angle = -5;
        this.totalOwnPointsText.skew.x = -0.08;
        this.addChild(this.totalOwnPointsText);
        this.totalTokenIcon = new Sprite(this.tournamentIntroResources.assets_views_tournament_intro_coin_png);
        this.totalTokenIcon.scale.set(0.48);
        this.addChild(this.totalTokenIcon);
        this.firstText = new Text(TournamentIntroPopupView.FOR_FIRST_TEXT, new DefaultTextStyle());
        this.firstText.style.fontSize = 80;
        this.firstText.angle = -5;
        this.firstText.skew.x = -0.08;
        this.addChild(this.firstText);
        this.winsText = new Text(TournamentIntroPopupView.WINNERS_TEXT, new DefaultTextStyle());
        this.winsText.style.fill = ["#a9fd0a", "#19d014"];
        this.winsText.style.fontSize = 80;
        this.winsText.angle = -5;
        this.winsText.skew.x = -0.08;
        this.addChild(this.winsText);
        this.firstPlaceText = new Text(TournamentIntroPopupView.FIRST_PLACE_TEXT, new DefaultTextStyle());
        this.firstPlaceText.style.fill = ["#fee201", "#ff9123"];
        this.firstPlaceText.style.fontSize = 120;
        this.firstPlaceText.angle = -5;
        this.firstPlaceText.skew.x = -0.08;
        this.addChild(this.firstPlaceText);
        this.willGetText = new Text(TournamentIntroPopupView.WILL_GET_TEXT, new DefaultTextStyle());
        this.willGetText.style.fontSize = 83;
        this.willGetText.angle = -5;
        this.willGetText.skew.x = -0.08;
        this.addChild(this.willGetText);
        const filter = new DropShadowFilter({ blur: 4, quality: 4, alpha: 0.8, color: 0x52dcff });
        filter.resolution = 0;
        this.secondAdditonalUsdcText = new Text(TournamentIntroPopupView.GET_OWN_POINTS_TEXT, new DefaultTextStyle());
        this.secondAdditonalUsdcText.style.fillGradientType = 0;
        this.secondAdditonalUsdcText.style.fillGradientStops = [0, 0.7, 0.8];
        this.secondAdditonalUsdcText.filters = [filter];
        this.secondAdditonalUsdcText.style.stroke = "#ffffff";
        this.secondAdditonalUsdcText.style.strokeThickness = 5;
        this.secondAdditonalUsdcText.style.fill = ["#FFE302", "#FF9024"];
        this.secondAdditonalUsdcText.style.fontSize = 160;
        this.secondAdditonalUsdcText.angle = -5;
        this.secondAdditonalUsdcText.skew.x = -0.08;
        this.secondAdditonalUsdcText.style.dropShadow = true;
        this.secondAdditonalUsdcText.style.dropShadowAngle = 0;
        this.secondAdditonalUsdcText.style.dropShadowBlur = 20;
        this.secondAdditonalUsdcText.style.dropShadowColor = "#52dcff";
        this.secondAdditonalUsdcText.style.dropShadowDistance = -3;
        this.addChild(this.secondAdditonalUsdcText);
        this.usdcIcon = new Sprite(this.tournamentIntroResources.assets_views_tournament_intro_coin_png);
        this.usdcIcon.filters = [filter];
        this.addChild(this.usdcIcon);
        this.glowedText = new Text(TournamentIntroPopupView.GLOWED_TEXT, new DefaultTextStyle());
        this.glowedText.filters = [filter];
        this.glowedText.style.align = "left";
        this.glowedText.style.fontFamily = GameFont.DEFAULT_FONT;
        this.glowedText.style.fontSize = 54;
        this.glowedText.style.dropShadow = true;
        this.glowedText.style.dropShadowAngle = 0;
        this.glowedText.style.dropShadowBlur = 20;
        this.glowedText.style.dropShadowColor = "#52dcff";
        this.glowedText.style.dropShadowDistance = -3;
        this.glowedText.angle = -5;
        this.glowedText.skew.x = -0.08;
        this.addChild(this.glowedText);
        this.hand = new Hand(this.commonResources);
        this.hand.scale.set(-0.65, 0.65);
        this.hand.angle = -98;
        this.addChild(this.hand);
        this.tournamentIcon = new Sprite(this.tournamentIntroResources.assets_views_tournament_intro_tournament_icon_png);
        this.addChild(this.tournamentIcon);
        this.button = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_button_green_png);
        this.button.onUp.add(this.onButtonClickHandle, this);
        this.addChild(this.button);
        const buttonText = new Text(TournamentIntroPopupView.BUTTON_TEXT, new ButtonTextStyle());
        buttonText.position.set(79, 39);
        this.button.defaultView.addChild(buttonText);
    }

    private async onButtonClickHandle(): Promise<void> {
        this.operationControls.complete(YouNoMoreOwnerSceneResult.NONE);
    }

    private layout(width: number, height: number): void {
        this.leftLight.position.set(0, 895);
        this.rightLight.position.set(width, 895);
        this.headerText.position.set(49, 198);
        this.headerAdditionalText.position.set(61, 388);
        this.giveText.position.set(242, 549);
        this.totalOwnPointsText.position.set(175, 617);
        this.totalTokenIcon.position.set(560, 580);
        this.firstText.position.set(126, 762);
        this.winsText.position.set(383, 739);
        this.firstPlaceText.position.set(50, 889);
        this.willGetText.position.set(537, 880);
        this.secondAdditonalUsdcText.position.set(110, 1030);
        this.usdcIcon.position.set(505, 950);
        this.glowedText.position.set(75, 1232);
        this.hand.position.set(282, 1276);
        this.tournamentIcon.position.set(601, 1276);
        this.button.position.set(229, 1551);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.button.onUp.remove(this.onButtonClickHandle, this);
        super.destroy(options);
    }
}

class DefaultTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 72,
            fill: 'ffffff'
        });
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            dropShadow: true,
            dropShadowAlpha: 0.5,
            dropShadowAngle: 0.5,
            dropShadowBlur: 5,
            dropShadowDistance: 4,
            fontSize: 78,
            leading: 15
        });
    }
}
